import type { World } from 'koota';
import { blankInputs, EMPTY_INPUT, type PlayerInput } from '../sim/input';
import { MatchState, RoundPhase, RoundState } from '../sim/traits';
import { captureArmsRaceProgress, restoreArmsRaceProgress } from '../sim/rules/armsRace';
import { captureKillStreaks, restoreKillStreaks } from '../sim/rules/killStreaks';
import { matchWinScores, restoreMatchWins } from '../sim/rules/scores';
import { MAX_PLAYERS } from '../core/players';
import {
  isPackedInput,
  mergePresses,
  packInput,
  samePacked,
  unpackInput,
  type MatchSlot,
  type PackedInput,
  type RoundRecovery,
} from './protocol';

/**
 * Bookkeeping for one online match, kept free of DOM and game-loop concerns so it can be unit
 * tested. `game.ts` owns the sim and asks this module what to step with and when.
 */

/** How many frames a client likes to have in hand: ~50 ms of slack against network jitter. */
export const CLIENT_BUFFER_TARGET = 3;

/** The host hashes its world every this many ticks and clients check theirs against it. */
export const HASH_EVERY_TICKS = 60;

/** Remote inputs are folded into the sim one per tick; a backlog past this is caught up at once. */
const REMOTE_BACKLOG = 2;
const REMOTE_QUEUE_CAP = 16;

/** Minimum gap between two input messages from a client (one sim tick). */
const SEND_INTERVAL_MS = 15;
export const INPUT_REFRESH_MS = 250;
export const REMOTE_INPUT_TIMEOUT_MS = 1000;
export const MAX_BUFFERED_FRAMES = 600;

export type OnlineRole = 'host' | 'client';

/**
 * Host side: the latest input each remote fighter sent. Inputs are applied in the order they arrived,
 * one per tick. Regular refreshes lease held inputs; a silent client becomes neutral within a second.
 */
export function createRemoteInputs(
  slots = MAX_PLAYERS,
  now: () => number = () => performance.now(),
) {
  const queues: PackedInput[][] = Array.from({ length: slots }, () => []);
  const held: PackedInput[] = blankInputs(slots).map(packInput);
  const receivedAt: number[] = Array.from({ length: slots }, () => -Infinity);
  const clear = (slot: number): void => {
    const q = queues[slot];
    if (q) q.length = 0;
    held[slot] = packInput(EMPTY_INPUT);
    receivedAt[slot] = -Infinity;
  };
  return {
    push(slot: number, packed: unknown): boolean {
      const q = queues[slot];
      if (!q || !isPackedInput(packed)) return false;
      receivedAt[slot] = now();
      q.push([...packed]);
      if (q.length > REMOTE_QUEUE_CAP) {
        // Fold the oldest into its successor so a press inside it still lands.
        const dropped = q.shift()!;
        q[0] = mergePresses(q[0]!, dropped);
      }
      return true;
    },
    /** The (still packed) input slot `slot` steps with this tick; the host broadcasts exactly this. */
    take(slot: number): PackedInput {
      if (now() - (receivedAt[slot] ?? -Infinity) >= REMOTE_INPUT_TIMEOUT_MS) clear(slot);
      const q = queues[slot];
      if (!q || q.length === 0) return held[slot] ?? packInput(EMPTY_INPUT);
      let next = q.shift()!;
      if (q.length > REMOTE_BACKLOG) {
        // Behind: fold everything but the newest into this tick (every press survives) and leave the
        // newest for the next tick, so a release recorded there is not swallowed by the merge.
        const newest = q[q.length - 1]!;
        for (let i = 0; i < q.length - 1; i++) next = mergePresses(q[i]!, next);
        q.length = 0;
        q.push(newest);
      }
      held[slot] = next;
      return next;
    },
    /** The peer left: their fighter stands still from now on. */
    clear,
    pending(slot: number): number {
      return queues[slot]?.length ?? 0;
    },
  };
}

export type RemoteInputs = ReturnType<typeof createRemoteInputs>;

/**
 * Client side: turns the local input stream into as few messages as possible without losing a tap.
 * A message goes out when the quantized input differs from what the host holds, at most once per
 * sim tick; any button pressed between two sends is folded into the next one.
 */
export function createInputSender(send: (packed: PackedInput) => void) {
  let lastSent: PackedInput | null = null;
  let pressedSince = 0;
  let nextAt = 0;
  let lastSentAt = -Infinity;
  return {
    update(input: PlayerInput, nowMs: number): void {
      const p = packInput(input);
      pressedSince |= p[3];
      const candidate: PackedInput = [p[0], p[1], p[2], p[3] | pressedSince];
      if (
        nowMs < nextAt ||
        (samePacked(lastSent, candidate) && nowMs - lastSentAt < INPUT_REFRESH_MS)
      )
        return;
      send(candidate);
      lastSent = candidate;
      pressedSince = 0;
      nextAt = nowMs + SEND_INTERVAL_MS;
      lastSentAt = nowMs;
    },
    /** Menus and focus loss must release controls immediately, including presses awaiting the next send. */
    neutral(nowMs: number): void {
      const p = packInput(EMPTY_INPUT);
      send(p);
      lastSent = p;
      pressedSince = 0;
      nextAt = nowMs + SEND_INTERVAL_MS;
      lastSentAt = nowMs;
    },
    get lastSent(): PackedInput | null {
      return lastSent;
    },
  };
}

/**
 * How many ticks a client steps this frame: what its own clock asks for, limited to the frames it
 * has, plus a burst when it has fallen far behind (a tab that was in the background).
 */
export function clientSteps(
  desired: number,
  buffered: number,
  target = CLIENT_BUFFER_TARGET,
): number {
  if (buffered === 0) return 0;
  let n = Math.min(desired, buffered);
  if (buffered > 30) n = Math.max(n, Math.min(buffered - target, buffered > 300 ? 60 : 10));
  return n;
}

/**
 * Nudge the client clock so the buffer hovers around its target instead of draining to zero (a stall)
 * or piling up (extra latency).
 */
export function pacingScale(buffered: number, target = CLIENT_BUFFER_TARGET): number {
  if (buffered > target + 3) return 1.1;
  if (buffered > target + 1) return 1.03;
  if (buffered < target - 1) return 0.97;
  return 1;
}

export type OnlineMatch = {
  matchId: string;
  role: OnlineRole;
  /** The fighter this browser steers, or -1 when watching from the lobby. */
  localSlot: number;
  slots: MatchSlot[];
  names: string[];
  /** Host: inputs from remote fighters. */
  remote: RemoteInputs;
  /** Host: frames stepped this render frame, flushed as one message. */
  outbox: PackedInput[][];
  outboxStart: number;
  /** Client: authoritative frames not yet stepped. */
  inbox: PlayerInput[][];
  /** Client: the tick the next inbox frame belongs to (gap detection). */
  nextTick: number;
  /** Client: hashes the host published, checked as the client reaches each tick. */
  hashes: Map<number, string>;
  lastHashOk: number;
  desync: boolean;
  lastResyncAt: number;
  recovering: boolean;
  recoveryNoticeUntil: number;
  /** Client: local input outbound. */
  sender: ReturnType<typeof createInputSender> | null;
  /** Client: the host did not step for a while (its tab is hidden, or the link is bad). */
  stalledSince: number;
};

export function createOnlineMatch(
  role: OnlineRole,
  slots: MatchSlot[],
  myPeerId: number,
  sendInput: (p: PackedInput) => void,
  matchId = '',
): OnlineMatch {
  return {
    matchId,
    role,
    localSlot: slots.findIndex((s) => s.peer === myPeerId),
    slots,
    names: slots.map((s) => s.name),
    remote: createRemoteInputs(slots.length),
    outbox: [],
    outboxStart: 0,
    inbox: [],
    nextTick: 0,
    hashes: new Map(),
    lastHashOk: 0,
    desync: false,
    lastResyncAt: -Infinity,
    recovering: false,
    recoveryNoticeUntil: 0,
    sender: role === 'client' ? createInputSender(sendInput) : null,
    stalledSince: 0,
  };
}

/** Client: file a batch of authoritative frames; returns false if the stream skipped ticks. */
export function acceptFrames(
  match: OnlineMatch,
  start: number,
  frames: PackedInput[][],
  hash?: { tick: number; h: string },
): boolean {
  // A gap cannot be repaired by appending later inputs at the wrong tick. Wait for a fresh round.
  if (
    match.nextTick !== start ||
    match.inbox.length + frames.length > MAX_BUFFERED_FRAMES ||
    frames.some((frame) => frame.length !== match.slots.length || !frame.every(isPackedInput))
  )
    return false;
  for (const frame of frames) match.inbox.push(frame.map(unpackInput));
  match.nextTick = start + frames.length;
  if (hash) match.hashes.set(hash.tick, hash.h);
  return true;
}

/** Client: compare the world after stepping `tick`; returns null when there was nothing to check. */
export function checkHash(match: OnlineMatch, tick: number, hashNow: () => string): boolean | null {
  const expected = match.hashes.get(tick);
  // Anything older than this tick can never be checked again.
  for (const t of match.hashes.keys()) if (t <= tick) match.hashes.delete(t);
  if (expected === undefined) return null;
  const ok = hashNow() === expected;
  if (ok) match.lastHashOk = tick;
  return ok;
}

export function captureRoundProgress(world: World): RoundRecovery {
  const match = world.get(MatchState);
  const round = world.get(RoundState);
  const armsRace = captureArmsRaceProgress(world);
  return {
    wins: matchWinScores(world),
    round: match?.round ?? 0,
    levelIndex: match?.levelIndex ?? 0,
    matchOver: round?.phase === RoundPhase.MatchOver,
    winner: round?.phase === RoundPhase.MatchOver ? (round.winner ?? -1) : -1,
    killStreaks: captureKillStreaks(world),
    ...(armsRace ? { armsRace } : {}),
  };
}

/** Apply progress to a NEW sim only. Countdown bypasses Loading's automatic next-arena rotation. */
export function applyRoundProgress(world: World, progress: RoundRecovery): void {
  const match = world.get(MatchState);
  const round = world.get(RoundState);
  if (!match || !round) return;
  world.set(MatchState, {
    ...match,
    round: progress.round,
    levelIndex: progress.levelIndex,
  });
  restoreMatchWins(world, progress.wins);
  world.set(RoundState, {
    ...round,
    phase: progress.matchOver ? RoundPhase.MatchOver : RoundPhase.Countdown,
    ticks: 0,
    winner: progress.winner,
  });
  if (progress.armsRace) restoreArmsRaceProgress(world, progress.armsRace);
  if (progress.killStreaks) restoreKillStreaks(world, progress.killStreaks);
}
